/**
 * Даты и время по НФТ-09 и ДАН-04: хранение в UTC, показ в часовом поясе филиала.
 * Формат даты — ДД.ММ.ГГГГ, время — ЧЧ:ММ (24 часа), дата с днём недели —
 * «среда, 30 сентября, 10:30» (RU) и «30 қыркүйек, сәрсенбі, 10:30» (KK, порядок CLDR).
 */
import { MONTHS_IN_DATE, WEEKDAYS_LONG, type AppLocale } from './locale-data';

/** Часовой пояс по умолчанию (ПОРТ-11): Asia/Almaty, UTC+5 с 01.03.2024. */
export const DEFAULT_TIME_ZONE = 'Asia/Almaty';

/** Календарная дата без времени и пояса (дата рождения, день в календаре). Месяц — 1…12. */
export interface CalendarDate {
  year: number;
  month: number;
  day: number;
}

export interface TimeOfDay {
  hour: number;
  minute: number;
}

export interface ZonedParts extends CalendarDate, TimeOfDay {
  second: number;
  /** День недели: 0 — понедельник … 6 — воскресенье. */
  weekday: number;
}

/** Момент времени: Date, ISO-строка (UTC) или миллисекунды. */
export type Instant = Date | string | number;

export interface ZoneOptions {
  /** Часовой пояс филиала (IANA). По умолчанию Asia/Almaty. */
  timeZone?: string;
}

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DISPLAY_DATE_RE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
const TIME_RE = /^(\d{1,2}):(\d{2})$/;
const WEEKDAY_INDEX: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

const partsFormatterCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = partsFormatterCache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      weekday: 'short',
    });
    partsFormatterCache.set(timeZone, formatter);
  }
  return formatter;
}

export function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/** Преобразует вход в Date; бросает RangeError на неверной дате. */
export function toInstant(value: Instant): Date {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) throw new RangeError(`Invalid instant: ${String(value)}`);
  return date;
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** Составные части момента времени в часовом поясе филиала. */
export function getZonedParts(value: Instant, timeZone: string = DEFAULT_TIME_ZONE): ZonedParts {
  const parts = partsFormatter(timeZone).formatToParts(toInstant(value));
  const read = (type: Intl.DateTimeFormatPartTypes): string => parts.find((part) => part.type === type)?.value ?? '0';
  const hour = Number(read('hour')) % 24;
  return {
    year: Number(read('year')),
    month: Number(read('month')),
    day: Number(read('day')),
    hour,
    minute: Number(read('minute')),
    second: Number(read('second')),
    weekday: WEEKDAY_INDEX[read('weekday')] ?? 0,
  };
}

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

export function isValidCalendarDate(year: number, month: number, day: number): boolean {
  return (
    Number.isInteger(year) &&
    Number.isInteger(month) &&
    Number.isInteger(day) &&
    year >= 1900 &&
    year <= 2100 &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= daysInMonth(year, month)
  );
}

/** «2026-09-30» → CalendarDate или null. */
export function parseIsoDate(value: string): CalendarDate | null {
  const match = ISO_DATE_RE.exec(value.trim());
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  return isValidCalendarDate(year, month, day) ? { year, month, day } : null;
}

/** CalendarDate → «2026-09-30» (формат API для дат без времени). */
export function toIsoDate(date: CalendarDate): string {
  return `${String(date.year).padStart(4, '0')}-${pad2(date.month)}-${pad2(date.day)}`;
}

/** «30.09.2026» (допускается «3.9.2026») → CalendarDate или null. */
export function parseDisplayDate(value: string): CalendarDate | null {
  const match = DISPLAY_DATE_RE.exec(value.trim());
  if (!match) return null;
  const [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
  return isValidCalendarDate(year, month, day) ? { year, month, day } : null;
}

/** CalendarDate → «30.09.2026». */
export function formatCalendarDate(date: CalendarDate): string {
  return `${pad2(date.day)}.${pad2(date.month)}.${date.year}`;
}

function isCalendarDate(value: unknown): value is CalendarDate {
  return (
    typeof value === 'object' &&
    value !== null &&
    !(value instanceof Date) &&
    typeof (value as CalendarDate).year === 'number' &&
    typeof (value as CalendarDate).month === 'number' &&
    typeof (value as CalendarDate).day === 'number'
  );
}

/**
 * Дата «ДД.ММ.ГГГГ». Строка «ГГГГ-ММ-ДД» и CalendarDate — календарная дата без сдвига пояса
 * (например, дата рождения); момент времени показывается в поясе филиала.
 */
export function formatDate(value: Instant | CalendarDate, options: ZoneOptions = {}): string {
  if (isCalendarDate(value)) return formatCalendarDate(value);
  if (typeof value === 'string') {
    const calendar = parseIsoDate(value);
    if (calendar) return formatCalendarDate(calendar);
  }
  const parts = getZonedParts(value, options.timeZone ?? DEFAULT_TIME_ZONE);
  return formatCalendarDate(parts);
}

/** Время «ЧЧ:ММ» в поясе филиала. */
export function formatTime(value: Instant, options: ZoneOptions = {}): string {
  const parts = getZonedParts(value, options.timeZone ?? DEFAULT_TIME_ZONE);
  return `${pad2(parts.hour)}:${pad2(parts.minute)}`;
}

/** «30.09.2026, 10:30» в поясе филиала. */
export function formatDateTime(value: Instant, options: ZoneOptions = {}): string {
  const parts = getZonedParts(value, options.timeZone ?? DEFAULT_TIME_ZONE);
  return `${formatCalendarDate(parts)}, ${pad2(parts.hour)}:${pad2(parts.minute)}`;
}

export interface WeekdayFormatOptions extends ZoneOptions {
  /** Показывать время (по умолчанию — да). */
  withTime?: boolean;
  /** Год: всегда, никогда или только если не текущий (по умолчанию). */
  withYear?: boolean | 'auto';
  /** «Сейчас» для режима withYear='auto' (для тестов). */
  now?: Instant;
}

/**
 * Дата с днём недели: RU «среда, 30 сентября, 10:30», KK «30 қыркүйек, сәрсенбі, 10:30».
 * Год добавляется, если дата не в текущем году: RU «среда, 30 сентября 2026 г., 10:30»,
 * KK «2026 ж. 30 қыркүйек, сәрсенбі, 10:30».
 */
export function formatDateWithWeekday(value: Instant, locale: AppLocale, options: WeekdayFormatOptions = {}): string {
  const timeZone = options.timeZone ?? DEFAULT_TIME_ZONE;
  const parts = getZonedParts(value, timeZone);
  const withTime = options.withTime ?? true;
  const withYearOption = options.withYear ?? 'auto';
  const withYear =
    withYearOption === 'auto' ? getZonedParts(options.now ?? new Date(), timeZone).year !== parts.year : withYearOption;
  const weekday = WEEKDAYS_LONG[locale][parts.weekday] ?? '';
  const month = MONTHS_IN_DATE[locale][parts.month - 1] ?? '';
  const time = `${pad2(parts.hour)}:${pad2(parts.minute)}`;
  if (locale === 'kk') {
    const datePart = `${withYear ? `${parts.year} ж. ` : ''}${parts.day} ${month}, ${weekday}`;
    return withTime ? `${datePart}, ${time}` : datePart;
  }
  const datePart = `${weekday}, ${parts.day} ${month}${withYear ? ` ${parts.year} г.` : ''}`;
  return withTime ? `${datePart}, ${time}` : datePart;
}

/**
 * День и месяц словами (DESIGN.md 1.8): RU «27 сентября, 14:17», KK «27 қыркүйек, 14:17»; год — только если не текущий
 * («27 сентября 2025 г.», «2025 ж. 27 қыркүйек»). Без времени — withTime: false.
 */
export function formatDayMonth(value: Instant, locale: AppLocale, options: WeekdayFormatOptions = {}): string {
  const timeZone = options.timeZone ?? DEFAULT_TIME_ZONE;
  const parts = getZonedParts(value, timeZone);
  const withYearOption = options.withYear ?? 'auto';
  const withYear = withYearOption === 'auto' ? getZonedParts(options.now ?? new Date(), timeZone).year !== parts.year : withYearOption;
  const month = MONTHS_IN_DATE[locale][parts.month - 1] ?? '';
  const datePart = locale === 'kk' ? `${withYear ? `${parts.year} ж. ` : ''}${parts.day} ${month}` : `${parts.day} ${month}${withYear ? ` ${parts.year} г.` : ''}`;
  return (options.withTime ?? true) ? `${datePart}, ${pad2(parts.hour)}:${pad2(parts.minute)}` : datePart;
}

/** Смещение пояса относительно UTC в минутах в указанный момент (Asia/Almaty → +300). */
export function timeZoneOffsetMinutes(at: Instant, timeZone: string = DEFAULT_TIME_ZONE): number {
  const instant = toInstant(at);
  const parts = getZonedParts(instant, timeZone);
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  const truncated = Math.floor(instant.getTime() / 1000) * 1000;
  return Math.round((asUtc - truncated) / 60000);
}

/** «UTC+5», «UTC+5:30», «UTC−3» — подпись пояса филиала рядом с полями времени. */
export function formatTimeZoneOffset(timeZone: string = DEFAULT_TIME_ZONE, at: Instant = new Date()): string {
  const offset = timeZoneOffsetMinutes(at, timeZone);
  if (offset === 0) return 'UTC';
  const sign = offset > 0 ? '+' : '−';
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  return `UTC${sign}${hours}${minutes ? `:${pad2(minutes)}` : ''}`;
}

/**
 * Местные дата и время филиала → момент (UTC). Учитывает переходы на летнее время
 * (в Казахстане их нет, но пояс филиала может быть любым).
 */
export function zonedDateTimeToInstant(date: CalendarDate, time: TimeOfDay, timeZone: string = DEFAULT_TIME_ZONE): Date {
  const utcGuess = Date.UTC(date.year, date.month - 1, date.day, time.hour, time.minute, 0);
  const firstOffset = timeZoneOffsetMinutes(utcGuess, timeZone);
  let result = utcGuess - firstOffset * 60000;
  const secondOffset = timeZoneOffsetMinutes(result, timeZone);
  if (secondOffset !== firstOffset) result = utcGuess - secondOffset * 60000;
  return new Date(result);
}

/** Сегодняшняя дата в поясе филиала. */
export function todayInTimeZone(timeZone: string = DEFAULT_TIME_ZONE, now: Instant = new Date()): CalendarDate {
  const { year, month, day } = getZonedParts(now, timeZone);
  return { year, month, day };
}

export function compareCalendarDates(a: CalendarDate, b: CalendarDate): number {
  if (a.year !== b.year) return a.year < b.year ? -1 : 1;
  if (a.month !== b.month) return a.month < b.month ? -1 : 1;
  if (a.day !== b.day) return a.day < b.day ? -1 : 1;
  return 0;
}

export function isSameCalendarDate(a: CalendarDate | null | undefined, b: CalendarDate | null | undefined): boolean {
  return Boolean(a && b && compareCalendarDates(a, b) === 0);
}

export function addDays(date: CalendarDate, days: number): CalendarDate {
  const result = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
  return { year: result.getUTCFullYear(), month: result.getUTCMonth() + 1, day: result.getUTCDate() };
}

export function addMonths(date: CalendarDate, months: number): CalendarDate {
  const total = date.year * 12 + (date.month - 1) + months;
  const year = Math.floor(total / 12);
  const month = (total % 12) + 1;
  return { year, month, day: Math.min(date.day, daysInMonth(year, month)) };
}

/** День недели календарной даты: 0 — понедельник … 6 — воскресенье. */
export function weekdayOf(date: CalendarDate): number {
  return (new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay() + 6) % 7;
}

/** Сетка месяца для календаря: 6 недель по 7 дней, неделя начинается с понедельника. */
export function monthGrid(year: number, month: number): CalendarDate[][] {
  const first: CalendarDate = { year, month, day: 1 };
  const start = addDays(first, -weekdayOf(first));
  const weeks: CalendarDate[][] = [];
  for (let week = 0; week < 6; week += 1) {
    const days: CalendarDate[] = [];
    for (let day = 0; day < 7; day += 1) days.push(addDays(start, week * 7 + day));
    weeks.push(days);
  }
  return weeks;
}

/** «9:05», «09:05» → { hour: 9, minute: 5 } или null. */
export function parseTimeOfDay(value: string): TimeOfDay | null {
  const match = TIME_RE.exec(value.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return { hour, minute };
}

export function formatTimeOfDay(time: TimeOfDay): string {
  return `${pad2(time.hour)}:${pad2(time.minute)}`;
}

export function timeOfDayToMinutes(time: TimeOfDay): number {
  return time.hour * 60 + time.minute;
}

export function minutesToTimeOfDay(minutes: number): TimeOfDay {
  const normalized = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return { hour: Math.floor(normalized / 60), minute: normalized % 60 };
}

/** Разница в целых минутах/часах/днях для подписи «5 минут назад» (тексты — в i18n). */
export type RelativeUnit = 'now' | 'minute' | 'hour' | 'day';

export interface RelativeTime {
  unit: RelativeUnit;
  count: number;
}

/** Относительное время до 7 дней; дальше — null (показывается полная дата). */
export function relativeTime(value: Instant, now: Instant = new Date()): RelativeTime | null {
  const diffMs = toInstant(now).getTime() - toInstant(value).getTime();
  if (diffMs < 0) return { unit: 'now', count: 0 };
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return { unit: 'now', count: 0 };
  if (minutes < 60) return { unit: 'minute', count: minutes };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { unit: 'hour', count: hours };
  const days = Math.floor(hours / 24);
  if (days < 7) return { unit: 'day', count: days };
  return null;
}
